import React, { Component, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Rollbar from 'mangools-commons/dist/configs/rollbar';
import Emoji from './Emoji';

const ERROR_TYPES = {
  CHUNK_LOAD: 'ChunkLoadError',
  OTHER: 'OTHER',
};

type AsyncLoaderErrorBoundaryProps = {
  children: ReactNode | ReactNode[];
  online: boolean;
};

type AsyncLoaderErrorBoundaryState = {
  errorType: string | null;
};

// React error boundary for the route subtree. It catches errors thrown while rendering
// the children and swaps them for a fallback screen, instead of React unmounting the
// whole tree. Errors thrown in event handlers are NOT caught here (no boundary can catch
// those) - they are reported by the Rollbar SDK's global captureUncaught handler and the
// rest of the app keeps working.
class AsyncLoaderErrorBoundary extends Component<AsyncLoaderErrorBoundaryProps, AsyncLoaderErrorBoundaryState> {
  state: AsyncLoaderErrorBoundaryState = {
    errorType: null,
  };

  static getDerivedStateFromError(error: Error): AsyncLoaderErrorBoundaryState {
    return {
      errorType: error?.name === ERROR_TYPES.CHUNK_LOAD ? ERROR_TYPES.CHUNK_LOAD : ERROR_TYPES.OTHER,
    };
  }

  componentDidCatch(error: Error) {
    if (this.props.online) {
      Rollbar.error(error);
    }
  }

  handleReload = () => {
    location.reload();
  };

  render() {
    if (!this.state.errorType) {
      return <>{this.props.children}</>;
    }

    const { errorType } = this.state;
    const { online } = this.props;

    return (
      <div className="kwfinder-main">
        <div className="uk-container uk-container-center">
          <div className="uk-flex uk-flex-middle uk-flex-center uk-position-cover">
            {online ? (
              errorType === ERROR_TYPES.CHUNK_LOAD ? (
                <div className="mg-padding-30">
                  <h1 className="font-36 mg-margin-b-30">
                    <FontAwesomeIcon icon="info-circle" className="mg-margin-r-10 uk-hidden-small"/>
                    Application outdated
                  </h1>
                  <p className="uk-margin-top font-24 color-grey mg-margin-b-30">
                    Looks like there is a <strong>new application version available</strong>.<br/>
                    To get the newest version, reload this page, please.
                  </p>
                  <button className="mg-btn is-blue" onClick={this.handleReload} type="button">
                    <FontAwesomeIcon icon="sync" className="mg-margin-r-10"/>
                    Reload page
                  </button>
                </div>
              ) : (
                <div className="mg-padding-30">
                  <h1 className="font-36 mg-margin-b-30">
                    Sorry, something went wrong <Emoji symbol="😢" label="crying-face"/>
                  </h1>
                  <p className="uk-margin-top font-24 color-grey mg-margin-b-30">
                    Looks like there was an <strong>error in the application</strong>.<br/>
                    If you see this repeatedly, contact our support please.
                  </p>
                </div>
              )
            ) : (
              <div className="mg-padding-30">
                <h1 className="font-36 mg-margin-b-30">
                  <FontAwesomeIcon icon="plug" className="mg-margin-r-10 uk-hidden-small"/>
                  Connection&nbsp;problem
                </h1>
                <p className="uk-margin-top font-24 color-grey mg-margin-b-30">
                  Looks like you have <strong>lost connection</strong> to the server or the
                  <br/>
                  <strong>request was blocked by a 3rd party application</strong>
                  <br/>
                  (e.g. browser extension like adBlock).
                </p>
                <button className="mg-btn is-blue" onClick={this.handleReload} type="button">
                  <FontAwesomeIcon icon="sync" className="mg-margin-r-10"/>
                  Reload page
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

type AsyncLoaderErrorBoundaryContainerProps = {
  children: ReactNode | ReactNode[];
};

const AsyncLoaderErrorBoundaryContainer = ({ children }: AsyncLoaderErrorBoundaryContainerProps) => {
  const online = useSelector((state: any) => state.ui.misc.onlineStatus);

  return (
    <AsyncLoaderErrorBoundary online={online}>
      {children}
    </AsyncLoaderErrorBoundary>
  );
};

export default AsyncLoaderErrorBoundaryContainer;
